import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { LayoutChangeEvent, StyleSheet, Text, View } from 'react-native';
import {
  CHART_COLORS,
  ChartLegend,
  showChartLegend,
  useChartStyles,
} from './chartTheme';
import { CartesianChartPayload } from './payloads';

/**
 * Bars running left to right, one row per category: the category reads in
 * full on the left instead of as a rotated axis label, which is what a
 * ranking of long names needs. Drawn with plain views — the gifted-charts
 * `horizontal` mode rotates the whole canvas, labels included.
 */
export const HorizontalBarChartWidget = memo(
  ({ data }: { data: CartesianChartPayload }) => {
    const s = useChartStyles();
    const h = useHorizontalBarStyles();
    // Every value label takes the widest one's width, so each bar is drawn
    // against the same track and their lengths stay proportional.
    const [valueWidth, setValueWidth] = useState(0);
    const onValueLayout = useCallback(
      (event: LayoutChangeEvent) => {
        const { width } = event.nativeEvent.layout;
        setValueWidth(current => Math.max(current, Math.ceil(width)));
      },
      [setValueWidth],
    );
    const series = data.series ?? [];
    const xKey = data.xKey ?? 'name';
    const items = data.data ?? [];
    if (items.length === 0 || series.length === 0) return null;

    const valueOf = (row: (typeof items)[number], key: string) =>
      Number(row[key] ?? 0);
    // A missing value (e.g. a blend side with no row) reads as a dash.
    const textOf = (row: (typeof items)[number], key: string) =>
      row[key] === undefined ? '—' : valueOf(row, key).toLocaleString('zh-CN');
    const max = Math.max(
      0,
      ...items.flatMap(row => series.map(sr => valueOf(row, sr.key))),
    );

    return (
      <View style={s.chartContainer}>
        {data.title ? <Text style={s.chartTitle}>{data.title}</Text> : null}
        <View style={h.rows}>
          {items.map((row, ri) => {
            const name = String(row[xKey] ?? '');
            return (
              <View
                key={ri}
                style={h.row}
                accessible
                accessibilityLabel={[
                  name,
                  ...series.map(
                    sr =>
                      `${series.length > 1 ? `${sr.name} ` : ''}${textOf(
                        row,
                        sr.key,
                      )}`,
                  ),
                ].join('，')}
              >
                <Text style={h.label} numberOfLines={1}>
                  {name}
                </Text>
                <View style={h.bars}>
                  {series.map((sr, si) => {
                    const value = valueOf(row, sr.key);
                    const ratio = max > 0 ? Math.max(value, 0) / max : 0;
                    return (
                      <View key={sr.key} style={h.barLine}>
                        <View style={h.track}>
                          <View
                            style={[
                              h.bar,
                              {
                                flex: ratio,
                                backgroundColor:
                                  sr.color ??
                                  CHART_COLORS[si % CHART_COLORS.length],
                              },
                            ]}
                          />
                          <View style={{ flex: 1 - ratio }} />
                        </View>
                        <Text
                          style={[h.value, { minWidth: valueWidth }]}
                          numberOfLines={1}
                          onLayout={onValueLayout}
                        >
                          {textOf(row, sr.key)}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </View>
        {showChartLegend(data) ? <ChartLegend series={series} /> : null}
      </View>
    );
  },
);

function useHorizontalBarStyles() {
  const { rpx } = useResponsiveContext();
  const s = useChartStyles();
  return useMemo(
    () =>
      StyleSheet.create({
        rows: { gap: rpx(8) },
        row: { flexDirection: 'row', alignItems: 'center', gap: rpx(8) },
        label: {
          ...StyleSheet.flatten(s.chartLegendText),
          width: '30%',
          textAlign: 'right',
        },
        bars: { flex: 1, gap: rpx(3) },
        barLine: { flexDirection: 'row', alignItems: 'center' },
        track: { flex: 1, flexDirection: 'row' },
        bar: { height: rpx(12), borderRadius: rpx(3), minWidth: rpx(2) },
        value: {
          ...StyleSheet.flatten(s.chartAxisText),
          marginLeft: rpx(4),
          flexShrink: 0,
          textAlign: 'right',
        },
      }),
    [rpx, s],
  );
}
